import * as React from 'react';
import Layout from 'antd/lib/layout/layout';
import { ReleaseStepOrStepRunComponentProps } from '@/components/ValidatedRouteComponents';
import { CalculatedDataPerSection, MonitoringSectionFilter } from './../monitoring/MonitoringContainer';
import { useSelector } from 'react-redux';
import { RootState } from '@/reducers/reducers';
import {
    selectIsReleaseFromScraper,
    selectMonitoringMetricsById,
    selectIsDemoInstanceOrDemoModeEnabled,
    selectAutomationById,
    selectStepRun,
} from '@/selectors/selectors';
import {
    AppMonitoringHealthMetricCategory,
    AutomationType,
    AutomationAutoStagedRollout,
    SettingType,
    ReleaseStepRolloutData,
    RolloutMonitoring,
} from '@/types';
import { massageMonitoringData, getIsAppleAppStore, getAutomationIdByType } from '@/helpers';
import { createAppSettingsRouteWithSettingType } from '@/helpers/routeHelpers';
import { RolloutMonitoringSummaryContent } from './../monitoring/MonitoringSummaryContent';
import { ReleaseEventsTimelineSection } from '../releaseOverview/ReleaseTimelineSection';
import { ReleaseTimelineSectionContext } from '../timelineEvents/EventsTimeline';

export const StepContentRollout: React.FC<ReleaseStepOrStepRunComponentProps> = ({
    orgId,
    appId,
    releaseId,
    releaseStepType,
    stepRunComponentProps,
    shouldShowFullEventTimeline,
}) => {
    const stepRunId = stepRunComponentProps?.stepRunId;
    const isDemoMode = useSelector((state: RootState) =>
        selectIsDemoInstanceOrDemoModeEnabled(state, orgId, releaseId),
    );
    const isReleaseFromScraper = useSelector((state: RootState) => selectIsReleaseFromScraper(state, releaseId)); // TODO-WF: read from step run data when scraper logic is migrated to WFs
    const stepRun = useSelector((state: RootState) => selectStepRun(state, stepRunId));
    const metricsById = useSelector((state: RootState) => selectMonitoringMetricsById(state, appId));
    const stepData: ReleaseStepRolloutData = { ...stepRun.data.summaryData, ...stepRun.data.complementaryData };
    const rolloutMonitoring: RolloutMonitoring = {
        automationRuns: stepRun.automationRuns,
        releaseAdoptions: stepData.releaseAdoptions,
        monitoringStatistics: stepData.monitoringStatistics,
        featureFlagTimeSeries: stepData.featureFlagTimeSeries,
        monitoringAutomations: stepData.monitoringAutomations,
        checklistItems: stepData.checklistItems,
        appStoreSelectedBuilds: stepData.appStoreSelectedBuilds,
        appStoreReviewData: stepData.appStoreReviewData,
        rolloutStartedAt: stepData.rolloutStartedAt,
    };

    const autoHaltUnstablePhasedReleasesAutomationId = getAutomationIdByType(
        stepRun.automationRuns,
        AutomationType.AutoHaltUnstablePhasedReleases,
    );
    const autoPromoteStablePhasedReleasesAutomationId = getAutomationIdByType(
        stepRun.automationRuns,
        AutomationType.AutoPromoteStablePhasedReleases,
    );
    const autoStagedRolloutAutomationId = getAutomationIdByType(
        stepRun.automationRuns,
        AutomationType.AutoStagedRollout,
    );
    const autoStagedRolloutAutomation = useSelector((state: RootState) =>
        selectAutomationById(state, appId, autoStagedRolloutAutomationId),
    ) as AutomationAutoStagedRollout;

    const displayWaitingForRelease = !stepData.releasedAt;
    const appStoreAvailableBuilds = rolloutMonitoring?.appStoreSelectedBuilds ?? [];
    const allMonitoringMetricsInSettings = Object.values(metricsById);
    const isAppleAppStore = getIsAppleAppStore(stepData.appStoreType);
    const hasPhasedReleaseState = !!stepData.phasedRelease;
    const automatedStagedRolloutEnabled = autoStagedRolloutAutomation?.enabled;
    const isAutomatedPhasedRollout = isAppleAppStore ? hasPhasedReleaseState : automatedStagedRolloutEnabled;
    const rolloutStartedAt = rolloutMonitoring?.rolloutStartedAt;
    const showLoading = !rolloutMonitoring;
    const monitoringSettingsLink = createAppSettingsRouteWithSettingType(orgId, appId, SettingType.HealthMetrics);
    const appStoreReviewData = rolloutMonitoring?.appStoreReviewData;
    const featureFlags = rolloutMonitoring?.featureFlagTimeSeries ?? [];

    const [selectedBuild, setSelectedBuild] = React.useState(null);
    const [selectedSection, setSelectedSection] = React.useState<MonitoringSectionFilter>(MonitoringSectionFilter.All);

    const handleMonitoringSectionFilterChange = (value: string) => {
        setSelectedSection(value as MonitoringSectionFilter);
    };

    React.useEffect(() => {
        if (appStoreAvailableBuilds.length > 0) {
            setSelectedBuild(appStoreAvailableBuilds[0]);
        }
    }, [stepRunComponentProps.stepRunId]);

    let groupedMonitoringStatisticsForCards = massageMonitoringData({
        monitoringStatistics: rolloutMonitoring?.monitoringStatistics ?? [],
        monitoringAutomations: rolloutMonitoring?.monitoringAutomations ?? [],
        metricsById,
    });

    let groupedMonitoringStatistics = massageMonitoringData({
        monitoringStatistics: rolloutMonitoring?.monitoringStatistics ?? [],
        monitoringAutomations: rolloutMonitoring?.monitoringAutomations ?? [],
        metricsById,
        shouldGroupCrashFreeStats: false,
    });

    const isMultiApk = appStoreAvailableBuilds?.length > 1;

    if (isMultiApk) {
        groupedMonitoringStatisticsForCards = groupedMonitoringStatisticsForCards.filter((stat) => {
            if (stat.category !== AppMonitoringHealthMetricCategory.Stability) {
                return true;
            }

            return stat.buildIdentifier === selectedBuild?.appStoreBuild.buildNumber;
        });

        groupedMonitoringStatistics = groupedMonitoringStatistics.filter((stat) => {
            if (stat.category !== AppMonitoringHealthMetricCategory.Stability) {
                return true;
            }

            return stat.buildIdentifier === selectedBuild?.appStoreBuild.buildNumber;
        });
    }

    const calculatedDataPerSection: CalculatedDataPerSection = groupedMonitoringStatistics.reduce(
        (acc, groupedMonitoringStatistic) => {
            const data = acc[groupedMonitoringStatistic.category] ?? {
                shouldShowAlert: false,
                groupedStatistics: [],
            };

            data.groupedStatistics.push(groupedMonitoringStatistic);
            acc[groupedMonitoringStatistic.category] = data;
            return acc;
        },
        {},
    );

    Object.entries(calculatedDataPerSection).forEach(([category]) => {
        calculatedDataPerSection[category].shouldShowAlert = allMonitoringMetricsInSettings.some((metric) => {
            metric.category === category;
        });
    });

    return (
        <Layout style={{ paddingTop: 15 }}>
            <ReleaseEventsTimelineSection
                orgId={orgId}
                appId={appId}
                releaseId={releaseId}
                stepRunId={stepRunComponentProps?.stepRunId}
                context={ReleaseTimelineSectionContext.ReleaseStep}
                releaseStepType={releaseStepType}
                shouldShowFullEventTimeline={shouldShowFullEventTimeline}
            />
            <RolloutMonitoringSummaryContent
                orgId={orgId}
                appId={appId}
                releaseId={releaseId}
                stepRunComponentProps={stepRunComponentProps}
                isReleaseFromScraper={isReleaseFromScraper}
                displayWaitingForRelease={displayWaitingForRelease}
                calculatedDataPerSection={calculatedDataPerSection}
                releaseVersion={stepData.internalVersionString}
                onShowFeatureFlag={() => {}}
                setSelectedBuild={setSelectedBuild}
                handleMonitoringSectionFilterChange={handleMonitoringSectionFilterChange}
                platform={stepData.platform}
                isDemoMode={isDemoMode}
                isAutomatedPhasedRollout={isAutomatedPhasedRollout}
                phasedRelease={stepData.phasedRelease}
                appStoreType={stepData.appStoreType}
                appStoreIntegrationId={stepData.appStoreIntegrationType}
                rolloutMonitoring={rolloutMonitoring}
                groupedMonitoringStatisticsForCards={groupedMonitoringStatisticsForCards}
                rolloutStartedAt={rolloutStartedAt}
                isPhased={hasPhasedReleaseState}
                isMultiApk={isMultiApk}
                autoHaltUnstablePhasedReleasesAutomationId={autoHaltUnstablePhasedReleasesAutomationId}
                autoPromoteStablePhasedReleasesAutomationId={autoPromoteStablePhasedReleasesAutomationId}
                autoStagedRolloutAutomationId={autoStagedRolloutAutomationId}
                selectedBuild={selectedBuild}
                isLoading={showLoading}
                section={selectedSection}
                monitoringSettingsLink={monitoringSettingsLink}
                releasedAt={stepData.releasedAt}
                appStoreSelectedBuilds={appStoreAvailableBuilds}
                stabilityIntegrationType={stepData.stabilityMonitoringIntegrationType}
                shouldShowDisabledUI={null}
                appStoreReviewData={appStoreReviewData}
                featureFlags={featureFlags}
                automationRuns={null}
            />
        </Layout>
    );
};
